import { AdjustmentsIcon, SearchIcon, XIcon } from "@heroicons/react/outline";
import React, { useEffect, useState } from "react";

import ApiService from "../../services/ApiService";
import EasyTrans from "./EasyTrans";
import Taglist from "../Taglist";
import {
  COMMON_LICENCE_IDS,
  SPECIAL_LICENCES,
} from "../registerwork/licencework/commonLicences";
import { useTranslation } from "react-i18next";

const selectClass =
  "block w-full py-2 px-3 border border-gray-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-fairblue-500 focus:border-fairblue-500 sm:text-sm";

const Controlbar = (props) => {
  const [open, setOpen] = useState(false);
  const [t] = useTranslation();
  const [licenses, setLicenses] = useState([]);

  // controlled filter values so we can show an active count + reset
  const [type, setType] = useState("");
  const [license, setLicense] = useState("");
  const [sort, setSort] = useState("asc");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await ApiService.getLicenceList();
        const byId = new Map(
          (result?.licenses || []).map((l) => [l.licenseId, l]),
        );
        // Only offer the licenses the register flow can actually produce, in
        // the curated order, labelled with their SPDX name when available.
        setLicenses(
          COMMON_LICENCE_IDS.map((id) => ({
            licenseId: id,
            name: byId.get(id)?.name || id,
          })),
        );
      } catch (error) {
        console.error("Error:", error);
      }
    };
    fetchData();
  }, []);

  const tagCount = props.selectedTags?.length || 0;
  const activeFilters =
    (type ? 1 : 0) + (license ? 1 : 0) + (sort !== "asc" ? 1 : 0) + tagCount;

  const reset = () => {
    setType("");
    setLicense("");
    setSort("asc");
    props.onSetWorkType("");
    props.onSetLicence("");
    props.onSetSort("asc");
    props.setSelectedTags([]);
  };

  return (
    <div className="bg-fairgray border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center gap-3">
          <label className="relative flex-1">
            <span className="sr-only">
              <EasyTrans>MyWorks.ControlBar.Search</EasyTrans>
            </span>
            <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-gray-400">
              <SearchIcon className="w-5 h-5" aria-hidden="true" />
            </span>
            <input
              type="text"
              name="search"
              id="search"
              className="block w-full rounded-full bg-white border border-gray-200 py-2.5 pl-11 pr-4 shadow-sm placeholder:text-gray-400 focus:outline-none focus:border-fairblue-500 focus:ring-2 focus:ring-fairblue-500 sm:text-sm"
              placeholder={t("MyWorks.ControlBar.Search")}
              onChange={(e) => props.onSetTitle(e.target.value)}
            />
          </label>

          <button
            type="button"
            onClick={() => setOpen(!open)}
            className={`relative inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-fairblue-500 ${
              open || activeFilters
                ? "border-fairblue-500 bg-fairblue-100 text-fairblue-700"
                : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            <AdjustmentsIcon className="h-5 w-5" aria-hidden="true" />
            <span className="hidden sm:inline">Filter</span>
            {activeFilters > 0 && (
              <span className="ml-0.5 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-fairblue-500 px-1.5 text-xs font-semibold text-white">
                {activeFilters}
              </span>
            )}
          </button>
        </div>

        {open && (
          <div className="mt-3 rounded-xl bg-white ring-1 ring-gray-900/5 shadow-sm p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <EasyTrans>RegisterWork.Element.CreationType</EasyTrans>
                </label>
                <select
                  className={selectClass}
                  value={type}
                  onChange={(e) => {
                    setType(e.target.value);
                    props.onSetWorkType(e.target.value);
                  }}
                >
                  <option value="">
                    {t("MyWorks.ControlBar.NoFilter")}
                  </option>
                  <option value="DOCUMENT">Document</option>
                  <option value="PICTURE">Picture</option>
                  <option value="AUDIO">Audio</option>
                  <option value="VIDEO">Video</option>
                  <option value="INTERACTIVE">Interactive</option>
                  <option value="SOFTWARE">Software</option>
                  <option value="HARDWARE">Hardware</option>
                  <option value="DATA">Data</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <EasyTrans>MyWorks.WorkListElement.License</EasyTrans>
                </label>
                <select
                  className={selectClass}
                  value={license}
                  onChange={(e) => {
                    setLicense(e.target.value);
                    props.onSetLicence(e.target.value);
                  }}
                >
                  <option value="">{t("MyWorks.ControlBar.NoFilter")}</option>
                  {SPECIAL_LICENCES.map((s) => (
                    <option key={s.id} value={s.id}>
                      {t(s.labelKey)}
                    </option>
                  ))}
                  {licenses.map((licence) => (
                    <option key={licence.licenseId} value={licence.licenseId}>
                      {licence.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <EasyTrans>Reihenfolge</EasyTrans>
                </label>
                <select
                  className={selectClass}
                  value={sort}
                  onChange={(e) => {
                    setSort(e.target.value);
                    props.onSetSort(e.target.value);
                  }}
                >
                  <option value="asc">Aufsteigend</option>
                  <option value="desc">Absteigend</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Tags
                </label>
                <Taglist setSelectedTags={props.setSelectedTags} />
              </div>
            </div>

            {activeFilters > 0 && (
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={reset}
                  className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-900 focus:outline-none"
                >
                  <XIcon className="h-4 w-4" aria-hidden="true" />
                  Reset
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Controlbar;
